import type { ReactNode } from 'react';
import { PrimitiveButton } from '@viquard/doc-ui';
import type { CalendarEvent, CalendarKindMap } from '../types.js';
import { resolveKind } from '../utils/kindTheme.js';
import { localTime } from '../utils/dateUtils.js';
import { useEventContextMenu } from '../hooks/useEventContextMenu.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { useCalendarMessages } from '../i18n.js';

export interface EventChipProps {
  readonly event: CalendarEvent;
  readonly kindTheme: CalendarKindMap;
  readonly onClick?: ((event: CalendarEvent) => void) | undefined;
  /** Show leading time (`HH:MM`) before the title. Off in compact month cells. */
  readonly showTime?: boolean | undefined;
  readonly viewerTz?: string | undefined;
  readonly selected?: boolean | undefined;
  readonly contextMenu?: EventMenuActions | undefined;
}

/** Compact, single-line event chip used in MonthView day cells (and elsewhere
 *  where vertical space is tight). Click target ≥ 28px high.
 *
 *  Color: every chip exposes its kind color via the `--vq-cal-kind` custom
 *  property so styles.css can mix it with the surface for theme-safe tints. */
export function EventChip(props: EventChipProps): ReactNode {
  const {
    event,
    kindTheme,
    onClick,
    showTime = false,
    viewerTz,
    selected = false,
    contextMenu,
  } = props;
  const messages = useCalendarMessages();
  const kind = resolveKind(kindTheme, event.kind);
  const time = showTime ? localTime(event.startsAt, viewerTz) : null;
  const interactive = onClick !== undefined;
  const cancelled = event.status === 'cancelled';
  const cm = useEventContextMenu(event, contextMenu ?? {}, {
    disabled: contextMenu === undefined,
    ariaLabel: messages.eventActionsAriaLabel,
  });

  const content = (
    <>
      {time !== null && <span className="vq-cal__chip-time">{time}</span>}
      <span className="vq-cal__chip-title">{event.title}</span>
    </>
  );

  const className = [
    'vq-cal__chip',
    cancelled ? 'is-cancelled' : '',
    selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (interactive) {
    return (
      <>
        <PrimitiveButton
          className={className}
          style={{ ['--vq-cal-kind' as string]: kind.color }}
          title={event.title}
          aria-pressed={selected}
          onClick={(e) => {
            e.stopPropagation();
            onClick(event);
          }}
          onContextMenu={cm.bind.onContextMenu}
        >
          {content}
        </PrimitiveButton>
        {cm.menu}
      </>
    );
  }
  return (
    <>
      <span
        className={className}
        style={{ ['--vq-cal-kind' as string]: kind.color }}
        title={event.title}
        onContextMenu={cm.bind.onContextMenu}
      >
        {content}
      </span>
      {cm.menu}
    </>
  );
}
